import { normalizePathname } from 'helpers/trackingDisabledPaths'

/** Auth / public paths that must not be persisted or restored as currentUrl. */
export const NON_RESTORABLE_PATHS = [
  '/login',
  '/forgot',
  '/reset',
  '/invite',
  '/verify',
  '/security',
  '/sign-up',
  '/session-error',
]

/**
 * @param {string | null | undefined} url pathname + optional search
 * @returns {boolean}
 */
export const isRestorableUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false
  }

  const pathname = normalizePathname(url.split('?')[0] || '')
  if (!pathname) {
    return false
  }

  return !NON_RESTORABLE_PATHS.includes(pathname)
}

/**
 * Whether a URL should be written to userSession.currentUrl.
 *
 * `/` is LoginContainer's post-login default. Persisting it (boot flush or
 * history listener after `router.push('/')`) overwrites the last real page
 * before `restoreCurrentUrl` can run.
 *
 * @param {string | null | undefined} url pathname + optional search
 * @returns {boolean}
 */
export const isPersistableUrl = (url) => {
  if (!isRestorableUrl(url)) {
    return false
  }

  const pathname = normalizePathname(url.split('?')[0] || '')
  return pathname !== '/'
}

/**
 * Build a query string from a react-router `location.query` object.
 * @param {Record<string, unknown> | undefined} query
 * @returns {string} including leading `?`, or empty string
 */
export const queryToSearch = (query) => {
  if (!query || typeof query !== 'object') {
    return ''
  }
  const params = new URLSearchParams()
  Object.keys(query).forEach((key) => {
    const value = query[key]
    if (value === undefined || value === null) {
      return
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null) {
          params.append(key, String(entry))
        }
      })
      return
    }
    params.append(key, String(value))
  })
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * @param {{ pathname?: string, search?: string, query?: Record<string, unknown> }} location
 * @returns {string} pathname + query string
 */
export const locationToUrl = (location) => {
  if (!location || !location.pathname) {
    return ''
  }
  const search = location.search || queryToSearch(location.query) || ''
  return `${location.pathname}${search}`
}

/**
 * @param {string} url pathname + optional search
 * @returns {{ pathname: string, search: string }}
 */
export const urlToLocation = (url) => {
  if (!url) {
    return { pathname: '/', search: '' }
  }
  const queryIndex = url.indexOf('?')
  if (queryIndex === -1) {
    return { pathname: url, search: '' }
  }
  return {
    pathname: url.slice(0, queryIndex),
    search: url.slice(queryIndex),
  }
}
